<template>
  <DashboardCard :title="title">
    <template #action>
      <div class="flex items-center space-x-2">
        <button
          v-if="showExport"
          @click="exportData"
          class="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <i class="material-icons text-sm">file_download</i>
        </button>
        <button
          v-if="showRefresh"
          @click="refreshData"
          class="text-gray-500 hover:text-gray-700 transition-colors"
          :disabled="loading"
        >
          <i class="material-icons text-sm" :class="{ 'animate-spin': loading }"
            >refresh</i
          >
        </button>
      </div>
    </template>

    <div class="overflow-hidden">
      <div v-if="loading && data.length === 0" class="text-center py-8">
        <i class="material-icons text-gray-400 text-3xl animate-spin"
          >refresh</i
        >
        <p class="text-gray-500 mt-2">Loading data...</p>
      </div>

      <div v-else-if="data.length === 0" class="text-center py-8">
        <i class="material-icons text-gray-400 text-3xl">inbox</i>
        <p class="text-gray-500 mt-2">No data available</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                v-for="column in columns"
                :key="column.key"
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                :class="column.headerClass"
              >
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="(row, index) in paginatedData"
              :key="index"
              class="hover:bg-gray-50 transition-colors"
            >
              <td
                v-for="column in columns"
                :key="column.key"
                class="px-6 py-4 whitespace-nowrap text-sm"
                :class="column.cellClass"
              >
                <div v-if="column.type === 'avatar'" class="flex items-center">
                  <img
                    v-if="getValue(row, column.key) && !column.avatarInitials"
                    :src="getValue(row, column.key)"
                    :alt="getValue(row, column.avatarName || 'name')"
                    class="h-8 w-8 rounded-full mr-3"
                  />
                  <div
                    v-else
                    class="h-8 w-8 rounded-full flex items-center justify-center mr-3 text-white text-xs font-semibold"
                    :style="{
                      backgroundColor: getAvatarColor(
                        getValue(row, column.avatarName || column.key)
                      )
                    }"
                  >
                    {{
                      getInitials(
                        getValue(row, column.avatarName || column.key)
                      )
                    }}
                  </div>
                  <span class="font-medium text-gray-900">
                    {{ getValue(row, column.avatarName || "name") }}
                  </span>
                </div>

                <div v-else-if="column.type === 'badge'">
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="
                      getBadgeClass(
                        getValue(row, column.key),
                        column.badgeColors
                      )
                    "
                  >
                    {{ formatBadgeValue(getValue(row, column.key), column) }}
                  </span>
                </div>

                <div
                  v-else-if="column.type === 'currency'"
                  class="text-gray-900 font-medium"
                >
                  {{ formatCurrency(getValue(row, column.key)) }}
                </div>

                <div v-else-if="column.type === 'date'" class="text-gray-500">
                  {{ formatDate(getValue(row, column.key)) }}
                </div>

                <div v-else-if="column.type === 'progress'" class="w-full">
                  <div class="flex items-center">
                    <div class="w-full bg-gray-200 rounded-full h-2 mr-2">
                      <div
                        class="bg-blue-600 h-2 rounded-full"
                        :style="{ width: getValue(row, column.key) + '%' }"
                      ></div>
                    </div>
                    <span class="text-xs text-gray-500"
                      >{{ getValue(row, column.key) }}%</span
                    >
                  </div>
                </div>

                <div v-else class="text-gray-900">
                  {{ getValue(row, column.key) }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <template #footer v-if="showPagination && totalPages > 1">
      <div class="flex items-center justify-between">
        <div class="text-sm text-gray-700">
          Showing {{ startIndex + 1 }} to {{ endIndex }} of
          {{ data.length }} results
        </div>
        <div class="flex items-center space-x-2">
          <button
            @click="previousPage"
            :disabled="currentPage === 1"
            class="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span class="text-sm text-gray-700">
            Page {{ currentPage }} of {{ totalPages }}
          </span>
          <button
            @click="nextPage"
            :disabled="currentPage === totalPages"
            class="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    </template>
  </DashboardCard>
</template>

<script>
import DashboardCard from "./DashboardCard.vue";

export default {
  name: "DataTable",
  components: {
    DashboardCard
  },
  props: {
    title: {
      type: String,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    showExport: {
      type: Boolean,
      default: true
    },
    showRefresh: {
      type: Boolean,
      default: true
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    itemsPerPage: {
      type: Number,
      default: 5
    }
  },
  data() {
    return {
      currentPage: 1
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.data.length / this.itemsPerPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.data.length);
    },
    paginatedData() {
      if (!this.showPagination) return this.data;
      return this.data.slice(this.startIndex, this.endIndex);
    }
  },
  methods: {
    getValue(row, key) {
      return key.split(".").reduce((obj, k) => obj?.[k], row);
    },
    getBadgeClass(value, badgeColors = {}) {
      const defaultColors = {
        success: "bg-green-100 text-green-800",
        warning: "bg-yellow-100 text-yellow-800",
        error: "bg-red-100 text-red-800",
        info: "bg-blue-100 text-blue-800",
        active: "bg-green-100 text-green-800",
        inactive: "bg-gray-100 text-gray-800",
        pending: "bg-yellow-100 text-yellow-800"
      };

      const colors = { ...defaultColors, ...badgeColors };
      return colors[value?.toLowerCase()] || "bg-gray-100 text-gray-800";
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD"
      }).format(value);
    },
    formatDate(value) {
      return new Date(value).toLocaleDateString();
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    exportData() {
      this.$emit("export");
    },
    refreshData() {
      this.$emit("refresh");
    },
    getAvatarColor(name) {
      if (!name) return "#9CA3AF"; // Default gray

      const colors = [
        "#3B82F6", // Blue
        "#10B981", // Green
        "#F59E0B", // Yellow
        "#EF4444", // Red
        "#8B5CF6", // Purple
        "#EC4899", // Pink
        "#06B6D4", // Cyan
        "#84CC16" // Lime
      ];

      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }

      return colors[Math.abs(hash) % colors.length];
    },
    getInitials(name) {
      if (!name) return "??";

      return name
        .split(" ")
        .map(word => word.charAt(0))
        .join("")
        .toUpperCase()
        .substring(0, 2);
    },
    formatBadgeValue(value, column) {
      if (!value) return value;

      // If it's a plan code, format it nicely
      if (column.key === "plan_code") {
        return value.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase());
      }

      return value;
    }
  }
};
</script>
